import { useMemo } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { useClikArtCategoriesQuery } from '@/api/hooks/use-clikart-categories'
import { useClikArtPlacementOverrides } from '@/hooks/use-clikart-placement-overrides'
import { useUnitOfMeasure } from '@/contexts/editor-settings-context'
import { resolveClikArtPlaced } from '@/lib/clikart/clikart-placement-session'
import { resolvePlacedClikArtId } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import { formatPointsForDisplay } from '@/lib/units/unit-conversion'

export type ClikArtPanelContext = {
  fileNameLabel: string
  categoryLabel: string
  dimensionsLabel: string
  placedLabel: string
}

/**
 * The General Info rows for the selected placed ClikArt. File name and print size come from the
 * shape; category and pixel size come from the library catalog once it has loaded (the
 * session-cached `/clikart/categories` query the gallery and its warmup already share), and read
 * "—" until then.
 */
export function useClikArtPanelContext(
  selectedClikArtShape: ShapeData | undefined
): ClikArtPanelContext {
  const unitOfMeasure = useUnitOfMeasure()
  const { data: catalog } = useClikArtCategoriesQuery()
  const placementOverrides = useClikArtPlacementOverrides()
  const artId = selectedClikArtShape ? resolvePlacedClikArtId(selectedClikArtShape) : null

  const catalogItem = useMemo(
    () => (artId ? catalog?.find((item) => item.artId === artId) : undefined),
    [catalog, artId]
  )

  const dimensionsLabel = useMemo(() => {
    if (!selectedClikArtShape) return '—'

    const printW = formatPointsForDisplay(selectedClikArtShape.width, unitOfMeasure)
    const printH = formatPointsForDisplay(selectedClikArtShape.height, unitOfMeasure)
    const pixelW = selectedClikArtShape.photoFill?.naturalWidth ?? catalogItem?.pixelWidth
    const pixelH = selectedClikArtShape.photoFill?.naturalHeight ?? catalogItem?.pixelHeight

    if (pixelW && pixelH) return `${printW} x ${printH} (${pixelW} x ${pixelH}px)`

    return `${printW} x ${printH}`
  }, [selectedClikArtShape, catalogItem, unitOfMeasure])

  // The same rule as the gallery's Placed badge — `resolveClikArtPlaced` decides; "—" is its
  // "nothing known yet" (no session record, no finished scan, and no catalog entry loaded).
  const placedLabel = useMemo(() => {
    if (!artId) return '—'

    const placed = resolveClikArtPlaced(artId, catalogItem?.placedFlag, placementOverrides)

    if (placed === undefined) return '—'

    return placed ? 'Yes' : 'Not placed'
  }, [artId, placementOverrides, catalogItem])

  if (!selectedClikArtShape) {
    return { fileNameLabel: '—', categoryLabel: '—', dimensionsLabel, placedLabel: '—' }
  }

  return {
    fileNameLabel: selectedClikArtShape.photoFill?.displayName || catalogItem?.displayName || '—',
    categoryLabel: catalogItem?.categoryName || '—',
    dimensionsLabel,
    placedLabel,
  }
}
